import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { formatRM, parseRM, senToInput } from '@/features/billing/money';
import { api, isApiError } from '@/lib/api';
import { PageContainer } from '@/layouts/PageContainer';
import styles from '@/pages/queue/queue.module.css';
import parity from '@/pages/ops/parity.module.css';
import { percentToBps } from './scheme-percentage';
export interface MemberScheme {
  configured: boolean;
  live: boolean;
  gpPriceSen: number | null;
  checkDiscountBps: number | null;
  videoPriceSen: number | null;
  validityMonths: number;
  wording: string;
  usage: {
    bills: number;
    discountSen: number;
    verified: number;
    pending: number;
  };
}
export function DiscountSchemePage() {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['member-scheme'],
    queryFn: ({ signal }) =>
      api
        .get<MemberScheme>('/fee-schedule/member-scheme', { signal })
        .then((r) => r.data),
  });
  const { hasPermission } = useSession();
  const [editing, setEditing] = useState(false);
  if (q.isLoading)
    return (
      <PageContainer>
        <LoadingState />
      </PageContainer>
    );
  if (q.error)
    return (
      <PageContainer>
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </PageContainer>
    );
  const d = q.data;
  if (!d)
    return (
      <PageContainer>
        <EmptyState title={t('operations.notConfigured')} />
      </PageContainer>
    );
  return (
    <PageContainer>
      <div className={parity.grid2}>
        <Card
          title={t('scheme.title')}
          actions={
            <Badge tone={d.live ? 'success' : 'neutral'}>
              {!d.configured
                ? t('operations.notConfigured')
                : d.live
                  ? t('scheme.live')
                  : t('scheme.paused')}
            </Badge>
          }
        >
          <dl className={parity.kv}>
            <dt>{t('scheme.scope')}</dt>
            <dd>{t('scheme.scopeBody')}</dd>
            <dt>{t('scheme.services')}</dt>
            <dd>
              {t('scheme.gpService')}:{' '}
              {d.gpPriceSen === null ? '—' : formatRM(d.gpPriceSen)}
              <br />
              {t('scheme.checkService')}:{' '}
              {d.checkDiscountBps === null
                ? '—'
                : `${d.checkDiscountBps / 100}%`}
              <br />
              {t('scheme.videoService')}:{' '}
              {d.videoPriceSen === null ? '—' : formatRM(d.videoPriceSen)}
            </dd>
            <dt>{t('scheme.validity')}</dt>
            <dd>{t('scheme.months', { count: d.validityMonths })}</dd>
            <dt>{t('scheme.verification')}</dt>
            <dd>{t('scheme.verificationBody')}</dd>
            <dt>{t('scheme.wording')}</dt>
            <dd>{d.wording}</dd>
          </dl>
          {hasPermission('discounts:manage') && (
            <Button size="sm" onClick={() => setEditing(true)}>
              {t('scheme.edit')}
            </Button>
          )}
        </Card>
        <Card title={t('scheme.thisMonth')}>
          <span className={parity.sub}>{t('scheme.usageWindow')}</span>
          {[
            [t('scheme.bills'), d.usage.bills],
            [t('scheme.savings'), formatRM(d.usage.discountSen)],
            [t('scheme.verified'), d.usage.verified],
            [t('scheme.pending'), d.usage.pending],
          ].map(([label, value]) => (
            <div key={label} className={parity.split}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </Card>
      </div>
      <Card title={t('scheme.rules')}>
        <dl className={parity.kv}>
          <dt>{t('scheme.allowed')}</dt>
          <dd>{t('scheme.allowedBody')}</dd>
          <dt>{t('scheme.notAllowed')}</dt>
          <dd>{t('scheme.notAllowedBody')}</dd>
        </dl>
      </Card>
      {editing && <SchemeEditor scheme={d} onClose={() => setEditing(false)} />}
    </PageContainer>
  );
}
function SchemeEditor({
  scheme: d,
  onClose,
}: {
  scheme: MemberScheme;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [live, setLive] = useState(d.live);
  const [gp, setGp] = useState(
    d.gpPriceSen === null ? '' : senToInput(d.gpPriceSen),
  );
  const [check, setCheck] = useState(
    d.checkDiscountBps === null ? '' : String(d.checkDiscountBps / 100),
  );
  const [video, setVideo] = useState(
    d.videoPriceSen === null ? '' : senToInput(d.videoPriceSen),
  );
  const [months, setMonths] = useState(String(d.validityMonths));
  const [wording, setWording] = useState(d.wording);
  const [error, setError] = useState<unknown>();
  const save = useMutation({
    mutationFn: (v: Omit<MemberScheme, 'configured' | 'usage'>) =>
      api.put<MemberScheme>('/fee-schedule/member-scheme', v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['member-scheme'] });
      void qc.invalidateQueries({ queryKey: ['fee-services'] });
      onClose();
      toast.show({ tone: 'success', title: t('common.saved') });
    },
  });
  const submit = () => {
    const price = (v: string) => (v.trim() ? parseRM(v) : null);
    const a = price(gp),
      b = price(video),
      c = check.trim() ? percentToBps(check) : null,
      n = Number(months);
    if (
      (gp.trim() && a === null) ||
      (video.trim() && b === null) ||
      (check.trim() && c === null) ||
      !Number.isInteger(n) ||
      n < 1 ||
      n > 120 ||
      !wording.trim() ||
      wording.trim().length > 120
    )
      return setError(new Error(t('scheme.invalid')));
    void save
      .mutateAsync({
        live,
        gpPriceSen: a,
        checkDiscountBps: c,
        videoPriceSen: b,
        validityMonths: n,
        wording: wording.trim(),
      })
      .catch(setError);
  };
  return (
    <Dialog
      open
      title={t('scheme.title')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={submit}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        <Field label={t('scheme.status')}>
          <Select
            value={live ? 'live' : 'paused'}
            onChange={(e) => setLive(e.target.value === 'live')}
            options={[
              { value: 'live', label: t('scheme.live') },
              { value: 'paused', label: t('scheme.paused') },
            ]}
          />
        </Field>
        {[
          {
            label: t('scheme.gp'),
            value: gp,
            setter: setGp,
            inputMode: 'decimal' as const,
          },
          {
            label: t('scheme.check'),
            value: check,
            setter: setCheck,
            inputMode: 'decimal' as const,
          },
          {
            label: t('scheme.video'),
            value: video,
            setter: setVideo,
            inputMode: 'decimal' as const,
          },
          {
            label: t('scheme.validity'),
            value: months,
            setter: setMonths,
            inputMode: 'numeric' as const,
          },
          {
            label: t('scheme.wording'),
            value: wording,
            setter: setWording,
            inputMode: 'text' as const,
          },
        ].map(({ label, value, setter, inputMode }) => (
          <Field key={label} label={label}>
            <Input
              value={value}
              inputMode={inputMode}
              maxLength={inputMode === 'text' ? 120 : undefined}
              onChange={(e) => setter(e.target.value)}
            />
          </Field>
        ))}
        <p>{t('scheme.scopeBody')}</p>
        {error !== undefined && (
          <ErrorState
            error={error}
            title={
              error instanceof Error && !isApiError(error)
                ? error.message
                : undefined
            }
          />
        )}
      </div>
    </Dialog>
  );
}
